import { Factory } from "@/data/dao/Factory";
import { OrderDao } from "@/data/dao/service/OrderDao";
import type { ICancellationPosition, ICancellationView } from "@/data/model/Cancellation";
import {
    CancellationMode,
    CancellationPositionType,
    CancellationReason,
    PaymentClient,
    PaymentProvider,
    RefundMode,
} from "@/data/model/Enums";
import { config, role } from "@/data/util/Config";
import { DateTime } from "@/data/util/DateTime";
import { Format } from "@/data/util/Format";

import { translate } from "@/shared/localization/language";
import { PaymentMethodUtil } from "@/shared/utils/PaymentMethodUtil";

import { i18n, isInRole } from "@/portal/Trans";
import { DataTable, type IDataTableConfig } from "@/portal/elements/DataTable";
import { TableFilter } from "@/portal/elements/TableFilter";
import { portalEvents } from "@/portal/events/portalEvents";
import { TransactionDetailsWindow } from "@/portal/modal/TransactionDetailsWindow";

export class CancellationTableController {
    private $element: JQuery;
    private dao: OrderDao;
    private dataTable: DataTable<ICancellationView>;
    private search: TableFilter;

    constructor(element: JQuery) {
        this.$element = element;

        this.dao = Factory.getInstance<OrderDao>(OrderDao);

        const tableConfig: IDataTableConfig<ICancellationView> = {
            columnMapping: [
                {
                    name: i18n("general.fields.date"),
                    property: "created",
                    styles: { width: "10%" },
                    formatter: (m, p) => DateTime.toPreciseDateTimeString(p),
                },
                {
                    name: i18n("general.entities.order"),
                    property: "orderId",
                    styles: { width: "5%" },
                    formatter: (m, p) => {
                        return $(`<a href="/order/${p}?lId=${m.orderLocationId}">${m.orderCode}</a>`).click((e) => {
                            e.stopPropagation();
                        });
                    },
                },
                {
                    name: i18n("modal.cancellation.payment"),
                    property: "paymentId",
                    styles: { width: "7%" },
                    formatter: (m, p) => {
                        if (p == null) return "";

                        return $(
                            '<a style="overflow: hidden; display: block; text-overflow: ellipsis; white-space: nowrap; max-width: 70px;" href="/payment/' +
                                p +
                                '" >' +
                                m.externalPaymentId +
                                "</a>"
                        ).click((e) => {
                            e.stopPropagation();
                            e.preventDefault();

                            this.openTransactionWindow(p);
                        });
                    },
                },
                {
                    name: i18n("cancellation.fields.mode"),
                    property: "mode",
                    styles: { width: "6%" },
                    formatter: (m, p) => {
                        let colorClass = "kt-badge--primary";
                        switch (p) {
                            case CancellationMode.Amount:
                                colorClass = "kt-badge--warning";
                                break;
                            case CancellationMode.Positions:
                                colorClass = "kt-badge--primary";
                                break;
                            case CancellationMode.WholeOrder:
                                colorClass = "kt-badge--danger";
                                break;
                        }

                        return (
                            '<div class="kt-badge ' +
                            colorClass +
                            ' kt-badge--inline">' +
                            i18n("enums.CancellationMode." + CancellationMode[p]) +
                            "</div>"
                        );
                    },
                },
                {
                    name: i18n("cancellation.fields.reason"),
                    property: "reason",
                    styles: { width: "7%" },
                    formatter: (m, reason) => {
                        if (reason == undefined) return "";

                        const colorClass = "kt-badge--danger";

                        const reasonText = i18n("enums.CancellationReason." + CancellationReason[reason]);
                        return `<div class="cancellation-reason-badge kt-badge ${colorClass} kt-badge--inline">${reasonText}</div>`;
                    },
                },
                {
                    name: i18n("cancellation.fields.positions"),
                    property: "positions",
                    styles: { width: "12.5%" },
                    formatter: (m, p) => {
                        if (m.mode === CancellationMode.WholeOrder) return i18n("cancellation.allPositions");

                        let txt = "";

                        for (const ai in p) {
                            const position: ICancellationPosition = p[ai];
                            if (position.positionType === CancellationPositionType.Tip)
                                txt += i18n("order.fields.tip") + ": " + position.amount.toFixed(2) + ", ";
                            else if (position.positionType === CancellationPositionType.OrderItem) {
                                txt +=
                                    (position.quantity > 1 ? position.quantity + "x " : "") +
                                    (position.orderItemInternalName !== undefined
                                        ? position.orderItemInternalName
                                        : Format.htmlEscape(translate(position.orderItemName))) +
                                    ": " +
                                    position.amount.toFixed(2) +
                                    ", ";
                            }
                        }

                        if (txt.length > 0) txt = txt.substring(0, txt.length - 2);

                        return txt;
                    },
                },
                {
                    name: i18n("cancellation.fields.cancelled"),
                    property: "cancelledAmount",
                    styles: { width: "6%", "text-align": "left" },
                    formatter: (m, p) => "CHF " + p.toFixed(2),
                },
                {
                    name: i18n("cancellation.fields.refunded"),
                    property: "refundedAmount",
                    styles: { width: "6%", "text-align": "left" },
                    formatter: (m, p) => "CHF " + p.toFixed(2),
                },
            ],
            filterProvider: {
                getFilter: () => this.search.getValue(),
                setFilter: (d) => this.search.setValue(d),
            },
            defaultSortAsc: false,
            locationDependend: true,
            defaultSortProperty: "created",
        };

        if (isInRole(role.Admin)) {
            tableConfig.columnMapping.push({
                name: i18n("transaction.fields.provider"),
                property: "provider",
                styles: { width: "5%" },
                formatter: (m) => {
                    const $container = $('<div class="payment-provider-name"></div>');

                    switch (m.provider) {
                        case PaymentProvider.Stripe:
                            $container.append(
                                $(
                                    '<a target="_blank" href="' +
                                        (config.stripeUrl + "payments/" + m.providerId) +
                                        '">' +
                                        i18n("enums.PaymentProvider." + PaymentProvider[m.provider]) +
                                        "</a>"
                                ).click((e) => {
                                    e.stopPropagation();
                                })
                            );
                            break;
                        case PaymentProvider.Wallee:
                            $container.append(
                                $(
                                    '<a target="_blank" href="' +
                                        (m.merchantId == null || m.merchantId === ""
                                            ? config.walleeUrlOld
                                            : config.walleeUrl + m.merchantId + "/") +
                                        "payment/refund/view/" +
                                        m.refundPspId +
                                        '">' +
                                        i18n("enums.PaymentProvider." + PaymentProvider[m.provider]) +
                                        "</a>"
                                ).click((e) => {
                                    e.stopPropagation();
                                })
                            );
                            break;
                        case PaymentProvider.PayTec:
                            $container.append(
                                $("<div>" + i18n("enums.PaymentProvider." + PaymentProvider[m.provider]) + "</div>")
                            );
                            break;
                        case null:
                            $container.append($("<div>" + RefundMode[RefundMode.Offline] + "</div>"));
                            break;
                    }
                    return $container;
                },
            });
        }

        tableConfig.columnMapping.push({
            name: i18n("transaction.fields.client"),
            property: "paymentClient",
            styles: { width: "6%" },
            formatter: (m, p) => {
                if (p === undefined || p == null) {
                    return "";
                } else {
                    return i18n("enums.PaymentClient." + PaymentClient[p]);
                }
            },
        });

        tableConfig.columnMapping.push({
            name: i18n("transaction.fields.paymentMethodButton"),
            property: "provider",
            styles: { width: "7.5%" },
            formatter: (m) => {
                if (m.paymentCode === "Voucher" || m.isDiscountPayment) {
                    return `<div class="ccinfo"><span class="brandicon" style="background-image:url('/assets/img/brands/percent.svg');"></span></div>`;
                } else if (m.customPaymentMethodImagePath != null && m.customPaymentMethodImagePath.length > 0) {
                    return `<div class="ccinfo"><span class="brandicon" style="background-image:url('${m.customPaymentMethodImagePath}');"></span></div>`;
                } else if (
                    m.predefinedPaymentMethodImagePath != null &&
                    m.predefinedPaymentMethodImagePath.length > 0
                ) {
                    if (m.predefinedPaymentMethodImagePath === "collective_icon") {
                        return '<div class="consolidated-icon"><div class="visa"></div><div class="mastercard"></div><div class="plus"><i class="fas fa-plus"></i></div></div>';
                    } else {
                        return `<div class="ccinfo"><span class="brandicon" style="background-image:url('${m.predefinedPaymentMethodImagePath}');"></span></div>`;
                    }
                }
            },
        });

        tableConfig.columnMapping.push({
            name: i18n("transaction.fields.paymentMethod"),
            property: "provider",
            styles: { width: "7.5%" },
            sortable: false,
            formatter: (m) => {
                return PaymentMethodUtil.generatePaymentMethodDivIconFromCancellationView("", m);
            },
        });

        tableConfig.columnMapping.push({
            name: i18n("cancellation.fields.refund"),
            property: "",
            styles: { width: "4.5%" },
            formatter: (m) => {
                if (m.refundMode == null) return "";

                let colorClass = "kt-badge--primary";
                switch (m.refundMode) {
                    case RefundMode.Online:
                        colorClass = "kt-badge--primary";
                        break;
                    case RefundMode.Offline:
                        colorClass = "kt-badge--warning";
                        break;
                }

                return (
                    '<div class="mb-1 mt-1 kt-badge ' +
                    colorClass +
                    ' kt-badge--inline">' +
                    i18n("enums.RefundMode." + RefundMode[m.refundMode!]) +
                    "</div><br/>"
                );
            },
        });

        tableConfig.columnMapping.push({
            name: i18n("modal.cancellation.user"),
            property: "userId",
            styles: { width: "10%" },
            formatter: (m) => {
                if (m.userId == null) {
                    return m.userName === undefined ? i18n("general.deletedUser") : Format.htmlEscape(m.userName);
                }

                if (isInRole(role.Admin)) {
                    if (m.userName === "Anonymer User") {
                        return Format.htmlEscape(i18n("general.anonymousUser"));
                    }

                    return $('<a href="/user/' + m.userId + '">' + m.userName + "</a>").click((e) => {
                        e.stopPropagation();
                    });
                } else {
                    return m.userName;
                }
            },
        });

        this.search = new TableFilter().appendTo(this.$element.find("> .kt-portlet__body.tablefilter"));
        this.search.addDropdownFilterFromEnum(
            "mode",
            CancellationMode,
            "enums.CancellationMode",
            i18n("cancellation.allModes")
        );
        this.dataTable = new DataTable<ICancellationView>(
            this.$element.find("> .kt-portlet__body > .kt-datatable"),
            {
                getData: (s) => this.dao.getCancellationView(s),
            },
            tableConfig
        );

        this.dataTable.listenTo(this.search);
        portalEvents.OnLocationChanged.add(() => {
            this.dataTable.loadData(true);
        }, this);
    }

    private openTransactionWindow(transactionId: string) {
        const window = new TransactionDetailsWindow(transactionId);
        window.show();
    }
}
